import { useQuery } from '@tanstack/react-query';
import { get } from '@/shared/api/api-client';
import type { DoctorWorkingHoursListItem } from '@/types/settings-api';

/**
 * Canonical React Query key for the doctor working-hours list cache.
 * Exported so the mutation flows shipped in follow-up PRs (add / edit /
 * delete) can invalidate this exact key without inlining the array
 * literal — same factory pattern as `practitionerByIdQueryKey` and
 * `providerBillingSettingsQueryKey`.
 */
export function doctorWorkingHoursListQueryKey(
  doctorSetupId: string | null | undefined,
): readonly [string, string, string | null | undefined] {
  return ['settings', 'doctor-working-hours-list', doctorSetupId];
}

/**
 * List of working-hour profiles for a practitioner —
 * PROD-762 PR-A (Schedule tab foundation).
 *
 * GET /api/Doctor/WorkingHoursList/{doctorSetupId}
 *
 * Flutter source: `settings_data_source_impl.dart:1083`
 * (`fetchDoctorWorkingHoursList`).
 *
 * **Note the asymmetric shape**: the GET list response is a DIFFERENT
 * envelope from the canonical edit shape returned by
 * `GET /api/Doctor/WorkingHour/{workingHourId}`. The list uses:
 *   - `workingHoursId` (PLURAL) keyed id
 *   - `practiceWorkingSlots[]` slot array with combined-format time
 *     strings (`"08:00 AM"`) and the typo'd `weedayId`
 *   - `startDate` / `endDate` as `"dd-MM-yyyy"` format
 *
 * The PUT-body shape uses `workingHourId` (singular), `workingHoursRequest[]`,
 * split-format time strings, and no-Z ISO dates. See
 * `DoctorWorkingHoursListItem` vs `DoctorWorkingHour` for the full type
 * diff. Wire shape verified against live dev cURL 2026-05-15.
 *
 * This list endpoint feeds the Schedule tab's Working Hours section
 * (display only). Edit flows fetch the canonical record by id before
 * opening the editor (PR-B).
 */
export function useDoctorWorkingHoursList(
  doctorSetupId: string | null | undefined,
) {
  return useQuery({
    queryKey: doctorWorkingHoursListQueryKey(doctorSetupId),
    queryFn: ({ signal }) => {
      if (!doctorSetupId) {
        throw new Error(
          'useDoctorWorkingHoursList called without doctorSetupId',
        );
      }
      return get<DoctorWorkingHoursListItem[]>(
        `/api/Doctor/WorkingHoursList/${doctorSetupId}`,
        { signal },
      );
    },
    enabled: !!doctorSetupId,
  });
}
